import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import './technical-about.css';
import {
  IconBrowser,
  IconCode,
  IconDatabase,
  IconDocker,
  IconLayers,
  IconLock,
  IconMicrophone,
  IconNest,
  IconQueue,
  IconReact,
  IconRedis,
  IconServer,
  IconShield,
  IconStorage,
  IconTarget,
} from './TechnicalAboutIcons';

function SectionHead({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="tech-about__section-head">
      <div className="tech-about__section-icon">{icon}</div>
      <h2 className="tech-about__section-title">{title}</h2>
    </div>
  );
}

function StackCard({
  icon,
  title,
  children,
  badges,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  badges?: string[];
}) {
  return (
    <div className="tech-about__stack-card">
      <div className="tech-about__stack-icon">{icon}</div>
      <div className="tech-about__stack-body">
        <h3 className="tech-about__stack-title">{title}</h3>
        <p className="tech-about__stack-text">{children}</p>
        {badges && badges.length > 0 && (
          <div className="tech-about__stack-badges">
            {badges.map((b) => (
              <span key={b} className="tech-about__badge">
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ArchRow({ icon, label, text }: { icon: ReactNode; label: string; text: ReactNode }) {
  const rowStyle: CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-start' };
  return (
    <li style={rowStyle}>
      <span
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 8,
          background: 'rgba(37, 99, 235, 0.08)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </span>
      <span>
        <strong style={{ color: 'var(--text-primary)' }}>{label}</strong> — {text}
      </span>
    </li>
  );
}

export function TechnicalAboutPage() {
  return (
    <div className="tech-about">
      <div className="tech-about__back">
        <Link to="/transcriptions">← К журналу встреч</Link>
      </div>

      <header className="tech-about__hero">
        <h1>AXIUM Transcribe — техническая информация</h1>
        <p className="tech-about__hero-lead">
          Локальная платформа транскрибации, диаризации и суммаризации аудио/видео. Ниже — архитектура, стек технологий и
          возможности обработки речи на уровне приложения.
        </p>
      </header>

      <section className="tech-about__section">
        <SectionHead icon={<IconTarget size={20} />} title="Назначение" />
        <p className="tech-about__prose">
          Веб-приложение для автоматической расшифровки аудио и видео. Загружаете медиафайл — система извлекает звук,
          распознаёт речь (Whisper), определяет говорящих (pyannote) и формирует текстовую расшифровку с тайм-кодами.
          Обработка выполняется локально, без отправки данных на внешние сервисы.
        </p>
      </section>

      <section className="tech-about__section">
        <SectionHead icon={<IconLayers size={20} />} title="Архитектура" />
        <p className="tech-about__prose">
          Монорепозиторий на <strong>pnpm</strong>: фронтенд (SPA) обращается к REST API бэкенда; воркер обрабатывает
          асинхронные задачи через очереди; Python-сервисы выполняют распознавание и диаризацию речи.
        </p>
        <ul className="tech-about__arch-list">
          <ArchRow
            icon={<IconBrowser size={20} />}
            label="Frontend"
            text={<>одностраничное приложение (React, React Router) для загрузки файлов и просмотра расшифровок.</>}
          />
          <ArchRow
            icon={<IconServer size={20} />}
            label="API"
            text={<>REST API на NestJS: аутентификация, управление транскрипциями, выдача presigned-ссылок на хранилище.</>}
          />
          <ArchRow
            icon={<IconQueue size={20} />}
            label="Worker"
            text={
              <>
                сервис обработки фоновых задач (BullMQ): извлечение аудио (ffmpeg), вызов распознавания и диаризации, запись
                результатов.
              </>
            }
          />
          <ArchRow
            icon={<IconMicrophone size={20} />}
            label="Transcriber"
            text={
              <>
                Python-сервис (FastAPI + Whisper): распознавание речи, потоковый вывод сегментов и слов в формате NDJSON.
              </>
            }
          />
          <ArchRow
            icon={
              <span style={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                <IconDatabase size={16} />
                <IconRedis size={16} />
                <IconStorage size={16} />
              </span>
            }
            label="Данные"
            text={
              <>
                PostgreSQL через Prisma (транскрипции, сегменты, слова, статусы); Redis для очередей BullMQ; MinIO для
                хранения медиафайлов.
              </>
            }
          />
        </ul>
      </section>

      <section className="tech-about__section">
        <SectionHead icon={<IconCode size={20} />} title="Технологический стек" />
        <div className="tech-about__stack-grid">
          <StackCard
            icon={<IconReact size={26} />}
            title="Клиент"
            badges={['React 18', 'TypeScript', 'Vite', 'React Router', 'TanStack Query', 'RHF', 'Zod', 'Uppy']}
          >
            SPA с типизацией, клиентским роутингом, кэшем серверного состояния, формами с валидацией и потоковой загрузкой
            через tus.
          </StackCard>
          <StackCard
            icon={<IconNest size={26} />}
            title="API"
            badges={['NestJS 10', 'Fastify', 'Prisma', 'class-validator', 'cookie-parser', 'Swagger*']}
          >
            REST API, модульная структура, валидация входящих данных, presigned URL для хранилища, Swagger при включённой
            настройке.
          </StackCard>
          <StackCard
            icon={<IconQueue size={26} />}
            title="Worker"
            badges={['NestJS', 'BullMQ', 'Prisma', 'ffmpeg', 'ffprobe', 'MinIO SDK']}
          >
            Фоновая обработка задач: извлечение метаданных и аудиодорожек из медиафайлов, вызов сервисов распознавания и
            диаризации, сохранение результатов.
          </StackCard>
          <StackCard
            icon={<IconMicrophone size={26} />}
            title="Распознавание речи"
            badges={['Python', 'FastAPI', 'Whisper', 'faster-whisper', 'ffmpeg', 'NDJSON']}
          >
            Сервис транскрибации: Whisper для распознавания речи, потоковый вывод сегментов и слов с тайм-кодами.
          </StackCard>
          <StackCard
            icon={
              <span style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
                <IconDatabase size={20} />
                <IconRedis size={20} />
                <IconStorage size={20} />
              </span>
            }
            title="Данные"
            badges={['PostgreSQL', 'Prisma', 'Prisma Migrate', 'Redis', 'ioredis', 'MinIO']}
          >
            Основное хранилище — PostgreSQL и типобезопасный доступ через Prisma. Redis для распределённых очередей BullMQ.
            MinIO S3-совместимое объектное хранилище для медиафайлов.
          </StackCard>
          <StackCard
            icon={<IconLock size={26} />}
            title="Аутентификация"
            badges={['argon2', 'JWT access + refresh', 'HTTP-only cookies']}
          >
            Хеширование паролей пользователей (argon2); токены выдаются через защищённые cookies на путь API.
          </StackCard>
          <StackCard icon={<IconDocker size={26} />} title="Сборка и деплой" badges={['Docker', 'Compose', 'tusd', 'GPU*']}>
            Образы и compose-файлы в репозитории для локального и контейнерного запуска. GPU-профиль (CUDA) для ускорения
            распознавания.
          </StackCard>
        </div>
      </section>

      <section className="tech-about__section">
        <SectionHead icon={<IconShield size={20} />} title="Обработка и безопасность" />
        <ul className="tech-about__list">
          <li>
            <strong>Локальная обработка</strong> — все операции (извлечение аудио, распознавание, диаризация) выполняются на
            вашей инфраструктуре; медиафайлы и расшифровки не покидают систему.
          </li>
          <li>
            <strong>Хранилище</strong> — MinIO (S3-совместимое) для приватного хранения исходных файлов и извлечённого аудио;
            доступ через presigned URL с ограниченным TTL.
          </li>
          <li>
            <strong>Очереди задач</strong> — BullMQ на Redis обеспечивает надёжную обработку: retry при ошибках, отмена задач,
            логи статусов.
          </li>
          <li>
            <strong>Распознавание речи</strong> — Whisper (модель <code>large-v3</code>) через faster-whisper для эффективной
            транскрибации; поддержка CPU и GPU (CUDA).
          </li>
          <li>
            <strong>Диаризация</strong> — pyannote.audio (модель <code>pyannote-community/speaker-diarization-2.0</code>) для
            автоматического определения говорящих; требуется Hugging Face токен.
          </li>
          <li>
            <strong>Загрузка файлов</strong> — потоковая загрузка через tus (до 3 ГиБ); загрузка происходит напрямую в MinIO,
            минуя API.
          </li>
        </ul>
        <div className="tech-about__muted">
          Конкретные версии зависимостей и переменные окружения описаны в файлах проекта и <code>.env.example</code>. Веса
          моделей Whisper и pyannote скачиваются автоматически при первом запуске (требуется интернет и ~10 ГБ места).
        </div>
      </section>
    </div>
  );
}
